import { RenderBalanceInColor } from '@/components/features/accounts/account-utils'
import { DeleteMovementModal } from '@/components/features/accounts/DeleteMovementModal'
import { MovementsTable } from '@/components/features/accounts/MovementsTable'
import DatePicker from '@/components/features/shared/DatePicker'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Account,
  AccountStatus
} from '@/domain/accounts/account'
import { accountKeys, useAccount, useAccounts, useDeleteMovement, useMovementChart, useMovementCounts, useMovements } from '@/hooks/api/useAccounts'
import { useAgreement } from '@/hooks/api/useAgreements'
import { useDateRange } from '@/hooks/useDateRange'
import { useServerPagination } from '@/hooks/useServerPagination'
import { AccountStatistics } from '@/pages/accounts/AccountStatistics'
import { Paths } from '@/routes/paths'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'

import {
  ChevronRight,
  CreditCard,
  FileText,
  Loader2,
  Search,
  Users,
  Wallet, X
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'

export const AccountDetails = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const params = useParams({ strict: false }) as { accountId: string }
  const accountId = params.accountId

  const { pagination, setPagination, updateQueryParams, inputValue: descriptionFilter } = useServerPagination({
    pageParam: 'page',
    limitParam: 'limit',
    searchParam: 'descriptionFilter',
    defaultLimit: 20,
    apiPageStart: 1,
  })

  const { fromDate, toDate, setFromDate, setToDate, dateFrom, dateTo } = useDateRange()

  useEffect(() => {
    if (accountId) {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
    }
  }, [accountId]);

  const { data: account, isLoading: isLoadingAccount } = useAccount(accountId)
  const { data: userAccounts } = useAccounts(
    { userId: account?.userId, limit: 100, isMain: false, isCollector: false },
    { enabled: !!account?.userId }
  )
  const { data: agreement, isLoading: isLoadingAgreement } = useAgreement(account?.contractId || '')

  const deleteMovementMutation = useDeleteMovement()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [pendingDeleteMovement, setPendingDeleteMovement] = useState<{ movementId: string; accountId: string } | null>(null)

  const movementFilters = useMemo(() => ({
    accountId,
    startDate: fromDate,
    endDate: toDate,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    description: descriptionFilter || undefined,
  }), [accountId, fromDate, toDate, pagination.pageIndex, pagination.pageSize, descriptionFilter])

  const { data: paginatedMovements, isLoading: isLoadingMovements } = useMovements({
    accountId,
    filters: movementFilters
  })

  const { data: movementCounts } = useMovementCounts({
    accountId,
    filters: {
      accountId,
      startDate: movementFilters.startDate,
      endDate: movementFilters.endDate,
      description: movementFilters.description,
    }
  })

  const chartFilters = useMemo(() => ({
    accountId,
    startDate: fromDate,
    endDate: toDate,
    description: descriptionFilter || undefined,
  }), [accountId, fromDate, toDate, descriptionFilter])

  const { data: chartData, isLoading: isLoadingChart } = useMovementChart({
    accountId,
    filters: chartFilters
  })

  const handleDeleteRequest = (movement: any) => {
    setPendingDeleteMovement({ movementId: movement.id, accountId: movement.accountId })
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (pendingDeleteMovement) {
      deleteMovementMutation.mutate(pendingDeleteMovement, {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setPendingDeleteMovement(null)
        }
      })
    }
  }

  if (isLoadingAccount) {
    return <LoaderElement />
  }

  if (!account) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No se encontró la cuenta</p>
        <Button variant="link" onClick={() => navigate({ to: `${Paths.Management.Accounts}` })}>
          Volver a Gestión de Cuentas
        </Button>
      </div>
    )
  }

  // Calculate statistics from API counts
  const totalCredits = movementCounts?.totalCredits || 0
  const totalDebits = movementCounts?.totalDebits || 0
  const filteredBalance = movementCounts?.balance || 0

  const displayName = (account: Account) => {
    return `${account.accountNumber}${!account.isMain ? '' : ` - ${account.accountAlias}`}`
  }

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">Cuentas de Cliente</h1>
          <p className="text-xl text-primary font-medium flex items-center gap-2">
            <Users className="h-5 w-5" />
            {account.userName}
          </p>
        </div>
      </NavHeader>

      {userAccounts && userAccounts.data.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold tracking-tight text-muted-foreground flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Cuentas Disponibles
          </h2>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {userAccounts.data.map(acc => {
              const isSelected = acc.id === accountId
              return (
                <Card
                  key={acc.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20 bg-primary/[0.02]' : 'hover:border-primary/50 opacity-80 hover:opacity-100'} relative overflow-hidden`}
                  onClick={() => !isSelected && navigate({ to: `${Paths.Management.Accounts}/${acc.id}`, search: { dateFrom, dateTo } })}
                >
                  {isSelected && (
                    <div className="absolute top-0 right-0 p-1 bg-primary text-primary-foreground text-[10px] font-bold px-2 rounded-bl-lg">
                      SELECCIONADA
                    </div>
                  )}
                  <CardHeader className="px-4 py-3 flex flex-row items-center justify-between space-y-0">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium truncate" title={acc.accountAlias || `Cuenta ${acc.accountNumber}`}>
                        {acc.accountAlias || `Cuenta ${acc.accountNumber}`}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono">{acc.accountNumber}</span>
                    </div>
                    <CreditCard className={`h-4 w-4 flex-shrink-0 ml-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  </CardHeader>
                  <CardContent className="px-4 pb-3 pt-0 flex items-baseline justify-between">
                    <div className={`text-lg font-bold ${acc.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <RenderBalanceInColor balance={acc.currentBalance} />
                    </div>
                    {!isSelected && (
                      <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight text-primary flex items-center gap-2">
          Detalles de Cuenta Seleccionada
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-3 sm:col-span-2">
                  <p className="text-sm text-muted-foreground">Servicio</p>
                  <div className="font-medium text-base mt-1">
                    {isLoadingAgreement ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
                      agreement?.offeringName ? (
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded-md text-sm">{agreement.offeringName}</span>
                      ) : (
                        <span className="text-muted-foreground italic text-sm">N/A</span>
                      )
                    )}
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <p className="text-sm text-muted-foreground">Nº de Contrato</p>
                  <div className="font-medium font-mono text-base mt-1 flex items-center gap-2">
                    {isLoadingAgreement ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
                      agreement?.contractNumber ? (
                        <>
                          {account.contractId && (
                            <div className="flex items-center gap-2 cursor-pointer w-fit bg-gray-100 hover:bg-gray-200 rounded-xl px-2 py-1 transition-colors"
                              onClick={() => navigate({ to: `${Paths.Management.Contracts}/${account.contractId}` })}>
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span>{agreement.contractNumber}</span>
                            </div>
                          )}
                        </>
                      ) : <span className="text-muted-foreground font-sans text-sm italic">N/A</span>
                    )}
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <p className="text-sm text-muted-foreground">Número de Cuenta</p>
                  <p className="font-medium font-mono text-base mt-1">{displayName(account)}</p>
                </div>
                <div className="col-span-1 sm:col-span-1">
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge variant={account.status === AccountStatus.ACTIVE ? 'default' : 'secondary'} className="mt-1">
                    {account.status === AccountStatus.ACTIVE ? 'Activa' : 'Suspendida'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20 flex flex-col justify-center">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saldo Actual
              </CardTitle>
              <Wallet className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl`}>
                <RenderBalanceInColor balance={account?.currentBalance || 0} />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Balance exclusivo de esta cuenta
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
      <section className="pt-2 space-y-4">
        <Tabs defaultValue="movements">
          <TabsList className="grid w-full max-w-[400px] grid-cols-2">
            <TabsTrigger value="movements">Movimientos</TabsTrigger>
            <TabsTrigger value="statistics">Estadísticas</TabsTrigger>
          </TabsList>

          <TabsContent value="movements" className="space-y-4 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Últimos Movimientos</h2>
                <p className="text-muted-foreground">Registro de actividad reciente en la cuenta</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-[10px] uppercase text-muted-foreground font-bold">Saldo en Período seleccionado</p>
                <p className={`text-xl font-bold tracking-tight`}>
                  <RenderBalanceInColor balance={filteredBalance} />
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:w-64">
                <Label htmlFor="descriptionFilter" className="text-[10px] uppercase text-muted-foreground font-bold">Buscar concepto</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="descriptionFilter"
                    type="text"
                    placeholder="Filtrar por concepto..."
                    value={descriptionFilter}
                    onChange={(e) => {
                      updateQueryParams({ descriptionFilter: e.target.value || undefined, page: 1 })
                    }}
                    className="pl-8 h-8"
                  />
                  {descriptionFilter && (
                    <button
                      type="button"
                      onClick={() => updateQueryParams({ descriptionFilter: undefined, page: 1 })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="grid gap-1">
                  <Label htmlFor="dateFrom" className="text-[10px] uppercase text-muted-foreground font-bold">Desde</Label>
                  <DatePicker
                    date={fromDate}
                    onSelect={(date) => {
                      setFromDate(date)
                      updateQueryParams({ page: 1 })
                    }}
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="dateTo" className="text-[10px] uppercase text-muted-foreground font-bold">Hasta</Label>
                  <DatePicker
                    date={toDate}
                    onSelect={(date) => {
                      setToDate(date)
                      updateQueryParams({ page: 1 })
                    }}
                  />
                </div>
              </div>
            </div>

            <Card className="w-full">
              <CardContent className="">
                {isLoadingMovements ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <MovementsTable
                    paginated={paginatedMovements}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    onDeleteRequest={handleDeleteRequest}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="statistics" className="space-y-4 mt-6">
            {isLoadingChart ? <LoaderElement /> : <AccountStatistics totalCredits={totalCredits} totalDebits={totalDebits} chartData={chartData || []} dateFrom={dateFrom} dateTo={dateTo} />}
          </TabsContent>
        </Tabs>
      </section>
      <DeleteMovementModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        movementDescription={pendingDeleteMovement ? paginatedMovements?.data.find(m => m.id === pendingDeleteMovement.movementId)?.description : undefined}
      />
    </PageContainer>
  )
}